/**
 * Invited employee, step 2 (only when the org requires 2FA): "Add a second
 * step to sign in." (design: ob-admin.jsx EmpMfa). The card offers an
 * authenticator app or a code by secondary email; "Set it up later" is
 * offered only while the org's grace period has days left — with 0 days the
 * enforcement middleware would block them, so they must enrol now.
 */
import { useTranslation } from "react-i18next";
import { MfaSetup, ObMain, ObShell } from "../kit";
import { InviteeTopBar } from "./common";

export interface MemberMfaScreenProps {
  orgName: string;
  personName?: string | null;
  /** Days left in the org's 2FA grace period; null/0 ⇒ no "Set it up later". */
  graceDaysLeft: number | null;
  /** Masked secondary email codes would go to (null ⇒ the card asks for one). */
  secondaryEmailMasked?: string | null;
  onConfirmed: (recoveryCodes: string[]) => void;
  onLater: () => void;
}

export function MemberMfaScreen({ orgName, personName, graceDaysLeft, secondaryEmailMasked, onConfirmed, onLater }: MemberMfaScreenProps) {
  const { t } = useTranslation();
  const grace = graceDaysLeft ?? 0;

  return (
    <ObShell>
      <InviteeTopBar orgName={orgName} personName={personName} />
      <ObMain width={560}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16, padding: "0 4px" }} data-screen-label="Employee MFA">
          <p className="ob-eye">
            {orgName
              ? t("onboarding.invitee.member.mfaEyebrow", { defaultValue: "Required by {{org}}", org: orgName })
              : t("onboarding.invitee.member.mfaEyebrowNoOrg", "Required by your organization")}
          </p>
          <h1 className="ob-h1" style={{ fontSize: 30 }}>
            {t("onboarding.invitee.member.mfaTitle", "Add a second step to sign in.")}
          </h1>
          <p className="ob-p">
            {t("onboarding.invitee.member.mfaBody", "Your organization requires two-step verification to protect its data.")}
          </p>
        </div>
        <MfaSetup
          secondaryEmailMasked={secondaryEmailMasked ?? null}
          onConfirmed={onConfirmed}
          onLater={grace > 0 ? onLater : undefined}
          laterHint={
            grace > 0
              ? t("onboarding.invitee.member.laterHint", {
                  defaultValue: "{{count}} days left before it's required.",
                  count: grace,
                })
              : undefined
          }
        />
      </ObMain>
    </ObShell>
  );
}
